import { _ } from '../../../lib';
import { PromptConfirm } from '../confirm';
import { FormType, openFlyoutForm } from '../form';
import { normaliseChatMessage, normaliseChatMessages } from './message-utils';
import { autoResizeTextarea, clearCopyTickTimer, focusTextareaIfAllowed, getCanControlVisibility, getContextScopeKey, getIsPrivilegedView, getSelectedThreadID, getVisibilityText, } from './utils';
export const handlePaneClick = (rs, e) => {
    focusTextareaIfAllowed(rs, e.target);
};
export const handlePaneMouseDownCapture = (rs, e) => {
    focusTextareaIfAllowed(rs, e.target);
};
export const handleTextboxChange = (rs, e) => {
    autoResizeTextarea(e.target);
    rs.updateState({ messageText: e.target.value });
};
export const loadThreads = (rs) => {
    const requestKey = rs.refs.loadThreadsRequestKey.current + 1;
    rs.refs.loadThreadsRequestKey.current = requestKey;
    rs.updateState({ loading: true, error: '' });
    rs.props.requestLoadThreads({
        onDone: () => {
            if (rs.refs.loadThreadsRequestKey.current !== requestKey) {
                return;
            }
            rs.updateState({ loading: false });
        },
        onSuccess: data => {
            if (rs.refs.loadThreadsRequestKey.current !== requestKey) {
                return;
            }
            rs.updateState(s => ({
                threads: data.threads,
                messages: normaliseChatMessages(data.messages),
                canControlVisibility: getCanControlVisibility(data),
                selectedThreadID: getSelectedThreadID({
                    threads: data.threads,
                    selectedThreadID: s.selectedThreadID,
                    showInactive: s.sidebarView.showInactive,
                }),
            }));
        },
        onError: message => {
            if (rs.refs.loadThreadsRequestKey.current !== requestKey) {
                return;
            }
            rs.updateState({ error: message });
        },
    });
};
export const copyMessageToClipboard = (rs, message) => {
    if (message.Text == null) {
        return;
    }
    if (navigator.clipboard?.writeText == null) {
        rs.updateState({ copiedMessageID: null });
        return;
    }
    navigator.clipboard
        .writeText(message.Text)
        .then(() => {
        rs.updateState({ copiedMessageID: message.ID });
        clearCopyTickTimer(rs);
        rs.refs.copyTickTimer.current = window.setTimeout(() => {
            rs.updateState({ copiedMessageID: null });
            rs.refs.copyTickTimer.current = null;
        }, 2000);
    })
        .catch(() => {
        rs.updateState({ copiedMessageID: null });
    });
};
export const createThreadWithInitialMessage = (rs, input, handlers) => {
    const requestScopeKey = getContextScopeKey(rs.props);
    rs.props.requestCreateThreadWithInitialMessage(input, {
        onDone: () => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            handlers.onDone();
        },
        onSuccess: data => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            rs.updateState(s => ({
                threads: [data.thread, ...s.threads],
                messages: data.chatMessage != null
                    ? [...s.messages, normaliseChatMessage(data.chatMessage)]
                    : s.messages,
                selectedThreadID: data.thread.ID,
                error: '',
            }));
            handlers.onSuccess();
        },
        onError: message => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            handlers.onError(message);
        },
    });
};
export const sendMessage = (rs) => {
    if (rs.state.sending || rs.state.loading) {
        return;
    }
    const messageText = rs.state.messageText.trim();
    if (messageText === '') {
        return;
    }
    const selectedThreadID = rs.state.selectedThreadID;
    const requestScopeKey = getContextScopeKey(rs.props);
    if (selectedThreadID == null) {
        return;
    }
    rs.updateState({ sending: true, error: '' });
    rs.props.requestSendMessage({
        threadID: selectedThreadID,
        text: messageText,
        visibleToTarget: rs.state.canControlVisibility
            ? rs.state.visibleToTarget
            : true,
    }, {
        onDone: () => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            rs.updateState({ sending: false });
        },
        onSuccess: data => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            rs.updateState(s => ({
                messages: [...s.messages, normaliseChatMessage(data.chatMessage)],
                messageText: '',
            }));
            window.requestAnimationFrame(() => {
                autoResizeTextarea(rs.refs.textarea.current);
            });
        },
        onError: message => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            rs.updateState({ error: message });
        },
    });
};
const applyUpdatedThread = (rs, updatedThread) => {
    rs.updateState(s => {
        const threads = _.map(s.threads, thread => thread.ID === updatedThread.ID ? updatedThread : thread);
        return {
            threads,
            selectedThreadID: getSelectedThreadID({
                threads,
                selectedThreadID: s.selectedThreadID,
                showInactive: s.sidebarView.showInactive,
            }),
            error: '',
        };
    });
};
const requestThreadUpdate = (rs, threadID, changes, missingThreadMessage, onDone) => {
    const requestScopeKey = getContextScopeKey(rs.props);
    rs.props.requestUpdateThread({
        threadID,
        ...changes,
    }, {
        onDone: () => { },
        onSuccess: data => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            if (data.thread == null) {
                onDone(missingThreadMessage);
                return;
            }
            applyUpdatedThread(rs, data.thread);
            onDone(true);
        },
        onError: message => {
            if (getContextScopeKey(rs.props) !== requestScopeKey) {
                return;
            }
            rs.updateState({ error: message });
            onDone(message);
        },
    });
};
export const renameSelectedThread = (rs) => {
    const selectedThread = _.find(rs.state.threads, thread => thread.ID === rs.state.selectedThreadID);
    if (selectedThread == null || rs.props.isReadOnly || rs.state.loading) {
        return;
    }
    rs.updateState({ error: '' });
    openFlyoutForm({
        title: 'Rename Thread',
        size: [520, 320],
        formPrompt: 'Update thread title',
        lblWidth: 100,
        defaultValue: () => ({
            title: selectedThread.Title ?? '',
        }),
        fields: {
            title: FormType.Text({
                lbl: 'Thread title',
                placeholder: 'Enter thread title',
                maxLength: 200,
                nullable: true,
            }),
        },
        lblSave: 'Rename',
        onSave: (model, formCallback) => {
            requestThreadUpdate(rs, selectedThread.ID, { title: model.current.title?.trim() ?? '' }, 'Unable to rename thread', formCallback);
        },
    });
};
export const confirmToggleThreadResolved = (rs) => {
    const selectedThread = _.find(rs.state.threads, thread => thread.ID === rs.state.selectedThreadID);
    if (selectedThread == null || rs.props.isReadOnly || rs.state.loading) {
        return;
    }
    const isResolved = selectedThread.ResolvedAt != null;
    const actionLabel = isResolved ? 'reopen' : 'close';
    PromptConfirm({
        title: `${isResolved ? 'Reopen' : 'Close'} thread?`,
        msg: `Are you sure you want to ${actionLabel} this thread?`,
        yes: callback => {
            requestThreadUpdate(rs, selectedThread.ID, { resolved: !isResolved }, 'Unable to update thread status', callback);
        },
    });
};
export const handleTextboxKeyDown = (rs, e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage(rs);
    }
};
export const updateSelectedThread = (rs, threadID) => {
    if (threadID == null) {
        rs.updateState({ selectedThreadID: null });
        return;
    }
    rs.refs.shouldNudgeScrollToBottom.current = true;
    rs.updateState({
        selectedThreadID: threadID,
        error: '',
    });
};
export const updateSidebarView = (rs, sidebarView) => {
    rs.updateState(s => {
        const nextSidebarView = { ...s.sidebarView, ...sidebarView };
        return {
            sidebarView: nextSidebarView,
            selectedThreadID: getSelectedThreadID({
                threads: s.threads,
                selectedThreadID: s.selectedThreadID,
                showInactive: nextSidebarView.showInactive,
            }),
        };
    });
};
export const openCreateThreadForm = (rs) => {
    const canControlVisibility = getIsPrivilegedView(rs.props) && rs.state.canControlVisibility;
    const visibilityText = getVisibilityText(rs.props);
    rs.updateState({ error: '' });
    openFlyoutForm({
        title: 'New Thread',
        size: [700, 560],
        formPrompt: 'Create a new chat thread',
        lblWidth: 100,
        defaultValue: () => ({
            title: '',
            initialMessage: '',
            visibleToTarget: true,
        }),
        fields: {
            title: FormType.Text({
                lbl: 'Thread title',
                placeholder: 'Enter thread title',
                maxLength: 200,
                nullable: true,
            }),
            initialMessage: FormType.Textarea({
                lbl: 'Initial message',
                fullWidth: true,
                voicePrompt: 'Initial chat message',
                placeholder: 'Type your first message...',
                maxLength: 8000,
                rows: 8,
            }),
            visibleToTarget: FormType.Checkbox({
                lbl: visibilityText.createThreadFieldLabel,
                hidden: !canControlVisibility,
            }),
        },
        lblSave: 'Create Thread',
        onSave: (model, formCallback) => {
            createThreadWithInitialMessage(rs, {
                title: model.current.title,
                initialMessage: model.current.initialMessage,
                visibleToTarget: canControlVisibility
                    ? model.current.visibleToTarget
                    : true,
            }, {
                onDone: () => { },
                onError: message => {
                    formCallback(message);
                },
                onSuccess: () => {
                    formCallback(true);
                },
            });
        },
    });
};
//# sourceMappingURL=actions.js.map